<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  watch,
  computed,
  onBeforeMount,
  onBeforeUnmount,
} from "vue";
import { Howl } from "howler";
import WebFont from "webfontloader";
import { ICON_MAP } from "../../../utils/icons.js";
import { getAlertTemplate } from "./templates/registry.js";

// Renderable media types — anything else is treated as "no visual" so the
// alert never hangs the queue.
const RENDERABLE_MEDIA = ["image", "emote", "video", "icon"];
// Hard cap on how long a single alert can live, in case a sound/video never
// fires load or error. Forces the queue to advance.
const MAX_ALERT_MS = 20000;

// Storage base URL from environment
const STORAGE_BASE_URL = import.meta.env.VITE_STORAGE_URL || "";

/**
 * Get the URL for a sound file
 */
function getSoundUrl(sound) {
  if (sound.src) return sound.src;
  if (STORAGE_BASE_URL) {
    return `${STORAGE_BASE_URL}/users/${sound.channelId}/sounds/${sound.id}.ogg`;
  }
  // No fallback - storage URL must be configured
  console.warn("VITE_STORAGE_URL not configured");
  return "";
}

/**
 * Get the URL for a media file (image/video)
 */
function getMediaUrl(media) {
  // Scene media is { type, value }: value is { src } for external/Klipy GIFs,
  // { channelId, id } for uploaded media, or the emote id (string) for emotes.
  const value = media.value ?? {};
  // External/Klipy URL (also tolerate a flat media.src just in case)
  if (value.src) return value.src;
  if (media.src) return media.src;
  if (media.type === "emote") {
    return `https://static-cdn.jtvnw.net/emoticons/v2/${media.value}/static/light/3.0`;
  }
  if (media.type === "icon") {
    // value is the icon name (e.g. "gamepad"); resolve to a bundled asset.
    return ICON_MAP[media.value] || "";
  }
  const extension = media.type === "video" ? "webm" : "webp";
  if (STORAGE_BASE_URL) {
    return `${STORAGE_BASE_URL}/users/${value.channelId}/medias/${value.id}.${extension}`;
  }
  // No fallback - storage URL must be configured
  console.warn("VITE_STORAGE_URL not configured");
  return "";
}

const emits = defineEmits(["ended", "error", "started"]);

const props = defineProps({
  currentAlert: {
    type: Object,
    required: true,
  },
  fontScale: {
    type: Number,
    default: 1,
  },
});

const DEFAULT_MEDIA_DURATION = 5000;
// When an alert has no sound, keep it on screen for at least this long
// (and loop the video) so short clips don't flash by.
const MIN_NO_SOUND_DURATION = 5000;

const mediaDuration = ref(DEFAULT_MEDIA_DURATION);
const videoPlayer = ref(null);

// loading states

const soundLoaded = ref(false);
const visualLoaded = ref(false);
const loaded = computed(() => soundLoaded.value && visualLoaded.value);
watch(
  () => loaded.value,
  (l) => {
    show.value = !!l;
    if (howlInstance.value && l) {
      howlInstance.value.play();
    }
  }
);

// show alert once visual and sound are both loaded
const show = ref(false);

const soundEnded = ref(false);
const visualEnded = ref(false);
const ended = computed(() => soundEnded.value && visualEnded.value);

watch(
  () => ended.value,
  (e) => {
    if (e) {
      endCurrentEvent();
    }
  }
);

// Sound management
const howlInstance = ref(null);

// Load the alert's chosen Google font (right weights/styles) before showing.
// Must run for every alert kind — including templates, which have no
// <img>/<video> @load to trigger it, otherwise rare glyphs (q, j, z, x…) fall
// back to a system font while common ones use whatever's already cached.
function loadFont() {
  return new Promise((resolve) => {
    const text = props.currentAlert?.settings?.text || {};
    const family = text.fontFamily || "Inter";
    const style = Array.isArray(text.fontStyle) ? text.fontStyle : [];
    const families = [family];
    if (style.includes("bold")) {
      families.push(`${family}:700`);
    }
    if (style.includes("italic")) {
      families.push(`${family}:italic`);
    }
    if (style.includes("bold") && style.includes("italic")) {
      families.push(`${family}:700italic`);
    }
    try {
      WebFont.load({
        google: { families },
        active: () => resolve(),
        inactive: () => resolve(),
        classes: false,
      });
    } catch (e) {
      console.error(e);
      resolve();
    }
  });
}

function onMediaLoad() {
  if (videoPlayer.value) {
    mediaDuration.value = videoPlayer.value.duration * 1000;
  }
  // Show as soon as the media element is ready; load the font in parallel so a
  // slow/blocked WebFont call can never keep the alert invisible (glyphs swap
  // in once it resolves).
  visualLoaded.value = true;
  loadFont();
}

function onMediaEnd() {
  visualEnded.value = true;
}

function onMediaMetaDataLoaded() {
  // duration is in seconds; everything downstream works in ms.
  mediaDuration.value = videoPlayer.value.duration * 1000;
}

const start = ref(null);

let endedFired = false;
let safetyTimer = null;
// Timers from the show-watcher below; cleared on end/unmount so a half-played
// alert doesn't fire show=false / onMediaEnd after the component is gone.
let displayTimers = [];

function clearDisplayTimers() {
  displayTimers.forEach(clearTimeout);
  displayTimers = [];
}

function endCurrentEvent() {
  if (endedFired) return; // guard against double-shift of the queue
  endedFired = true;
  if (safetyTimer) clearTimeout(safetyTimer);
  clearDisplayTimers();
  emits("ended");
}

//for visual only
watch(
  () => show.value,
  (showAlert) => {
    if (showAlert) {
      start.value = Date.now();
      if (
        props.currentAlert.media &&
        props.currentAlert.media.type === "video" &&
        videoPlayer.value
      ) {
        videoPlayer.value.play();
      }
      if (
        props.currentAlert.media &&
        ["image", "emote", "icon"].includes(props.currentAlert.media.type)
      ) {
        if (props.currentAlert.sound) {
          visualEnded.value = true;
        }
      }
      // Hide the alert after its display duration. With no sound, show for at
      // least 5s and loop the video (see :loop in the template) so a short clip
      // doesn't freeze/cut out; with sound, the media's own duration drives it.
      if (props.currentAlert.media) {
        const displayDuration = props.currentAlert.sound
          ? mediaDuration.value
          : Math.max(mediaDuration.value, MIN_NO_SOUND_DURATION);
        displayTimers.push(
          setTimeout(() => {
            show.value = false;
          }, displayDuration - animationOutDuration.value)
        );
        displayTimers.push(
          setTimeout(() => {
            onMediaEnd();
          }, displayDuration)
        );
      }
    }
  }
);

const ANIMATION_DURATION = 300;

// A built-in "template" media (rendered in code) owns its own staging: the
// streamer's position + in/out transitions are ignored and locked to the
// template's declared values. null for regular (uploaded/emote/icon) media.
const templateConfig = computed(() =>
  props.currentAlert?.media?.type === "template"
    ? getAlertTemplate(props.currentAlert.media.value?.id)
    : null
);

const animationIn = computed(() => {
  if (templateConfig.value) return templateConfig.value.animationIn || "";
  return props.currentAlert?.settings?.animation.in || "";
});
const animationInDuration = computed(() => {
  return animationIn.value ? ANIMATION_DURATION : 0;
});
const animationOut = computed(() => {
  if (templateConfig.value) return templateConfig.value.animationOut || "";
  return props.currentAlert?.settings?.animation.out || "";
});
const animationOutDuration = computed(() => {
  return animationOut.value ? ANIMATION_DURATION : 0;
});
const direction = computed(() => {
  if (templateConfig.value) return templateConfig.value.direction;
  return props.currentAlert?.settings?.text.position || "bottom"; // "bottom" | "top" | "left" | "right" | "center"
});

const fontSize = computed(() => {
  let fontSize;
  // Templates always render text at "small" for a tidy card composition,
  // regardless of the streamer's size setting.
  const size = templateConfig.value
    ? "sm"
    : props.currentAlert?.settings?.text.fontSize;
  switch (size) {
    case "sm":
      fontSize = 1;
      break;
    case "md":
      fontSize = 1.5;
      break;
    case "lg":
      fontSize = 2;
      break;
    default:
      fontSize = 1.5;
      break;
  }
  return props.fontScale * fontSize;
});

const textColor = computed(() => {
  return props.currentAlert?.settings?.text.color || "red";
});

const fontFamily = computed(() => {
  return props.currentAlert?.settings?.text.fontFamily || "Inter";
});

const fontStyle = computed(() => {
  return {
    fontWeight: props.currentAlert?.settings?.text.fontStyle?.includes("bold")
      ? "bold"
      : "normal",
    fontStyle: props.currentAlert?.settings?.text.fontStyle?.includes("italic")
      ? "italic"
      : "normal",
    textDecoration: props.currentAlert?.settings?.text.fontStyle?.includes(
      "underline"
    )
      ? "underline"
      : "none",
  };
});

const highlightColor = computed(() => {
  return props.currentAlert?.settings?.textHighlight?.color || "red";
});

const textStyle = computed(() => {
  return {
    color: textColor.value,
    fontFamily: fontFamily.value,
    fontSize: fontSize.value + "rem",
    lineHeight: "1rem",
    ...fontStyle.value,
  };
});
function convertTemplateToText(template, data) {
  let _template = template;
  const keys = Object.keys(data).map((key) => "#" + key);
  if (keys.length === 0) {
    return template;
  }

  // use regex to replace all instances of the key with the value and split into an array while keeping the delimiters

  // generate regex from data keys

  const regex = new RegExp(`(${keys.join("|")})`, "g");
  const splitTemplate = _template
    .split(regex)
    .filter(Boolean)
    .map((part) => {
      if (keys.includes(part)) {
        return {
          type: "highlight",
          value: data[part.replace("#", "")],
        };
      }
      return {
        type: "text",
        value: part,
      };
    });
  return splitTemplate;
}

const streamerMessage = computed(() => {
  return convertTemplateToText(
    props.currentAlert?.settings?.textTemplate || "",
    props.currentAlert?.data || {}
  );
});

function calculateStrokeTextCSS(strokeWidth, strokeColor) {
  const shadows = [];
  for (let j = 0; j < strokeWidth; j++) {
    const steps = Math.pow(j + 2, strokeWidth > 10 ? 1 : 2);
    for (let i = 0; i < steps; i++) {
      const angle = (i * 2 * Math.PI) / steps;
      const cos = Math.round(10000 * Math.cos(angle)) / 10000;
      const sin = Math.round(10000 * Math.sin(angle)) / 10000;
      shadows.push(
        `calc(${j}px * ` + cos + `) calc(${j}px * ` + sin + `) 0 ${strokeColor}`
      );
    }
  }

  const res = shadows.join(", ");
  return res;
}

const textBorderStyle = computed(() => {
  if (props.currentAlert?.settings?.textOutline) {
    let textBorderSize;
    switch (props.currentAlert?.settings?.textOutline.width) {
      case "sm":
        textBorderSize = 2 * props.fontScale;
        break;
      case "md":
        textBorderSize = 4 * props.fontScale;
        break;
      case "lg":
        textBorderSize = 8 * props.fontScale;
        break;
      case "none":
      default:
        textBorderSize = 0;
        break;
    }

    return {
      color: textColor.value,
      fontFamily: fontFamily.value,
      fontSize: fontSize.value + "rem",

      lineHeight: "1rem",
      ...fontStyle.value,
      textShadow: calculateStrokeTextCSS(
        textBorderSize,
        props.currentAlert?.settings?.textOutline.color || "black"
      ),
    };
  }
  return {
    display: "none",
  };
});

onBeforeMount(() => {
  if (props.currentAlert.sound) {
    howlInstance.value = new Howl({
      src: [getSoundUrl(props.currentAlert.sound)],
      preload: true,
      autoplay: false,
      volume: 1,
      html5: true, //for cors
      onend: () => {
        soundEnded.value = true;
      },
      onload: () => {
        soundLoaded.value = true;
        // Guard: this can fire after the component unmounted (howlInstance nulled).
        if (
          howlInstance.value &&
          (!props.currentAlert.media ||
            ["image", "emote", "icon"].includes(props.currentAlert.media.type))
        ) {
          mediaDuration.value = howlInstance.value.duration() * 1000;
        }
      },
      onloaderror: (e) => {
        console.error(e);
        // A failed sound must not block the visual: mark it loaded+ended so the
        // `loaded` gate can still flip and the alert shows.
        soundLoaded.value = true;
        soundEnded.value = true;
      },
      onplayerror: (e) => {
        console.error(e);
        soundLoaded.value = true;
        soundEnded.value = true;
      },
      onunlock: () => {
        // Guard against a null instance: unlock fires on user gesture and can
        // arrive after the alert unmounted (howlInstance nulled in cleanup).
        if (howlInstance.value && howlInstance.value.state() === "loaded") {
          soundLoaded.value = true;
        }
      },
    });
    howlInstance.value.load();
  } else {
    soundLoaded.value = true;
    soundEnded.value = true;
  }

  if (templateConfig.value) {
    // Built-in template: no asset to fetch. Show immediately and load the font
    // in parallel (glyphs swap in once ready); the show-watcher timers then
    // drive its duration (durationMs).
    mediaDuration.value = templateConfig.value.durationMs;
    visualLoaded.value = true;
    loadFont();
  } else if (
    props.currentAlert.media &&
    RENDERABLE_MEDIA.includes(props.currentAlert.media.type)
  ) {
    //videoPlayer.value.load();
  } else {
    // No media, or a type we can't render (e.g. unknown) — treat as no visual
    // so the alert never waits forever on a load event that won't come.
    visualLoaded.value = true;
    visualEnded.value = true;
  }

  // Watchdog: if the alert never resolves (sound/video load or error never
  // fires), force it to end so the queue keeps moving.
  safetyTimer = setTimeout(endCurrentEvent, MAX_ALERT_MS);
});

onBeforeUnmount(() => {
  if (safetyTimer) clearTimeout(safetyTimer);
  clearDisplayTimers();
  // Release the audio buffer — otherwise every alert leaks one Howl over a
  // 24/7 overlay session.
  if (howlInstance.value) {
    howlInstance.value.stop();
    howlInstance.value.unload();
    howlInstance.value = null;
  }
  // Release the video so OBS/CEF stops holding the decoder/network for it.
  if (videoPlayer.value) {
    videoPlayer.value.pause();
    videoPlayer.value.removeAttribute("src");
    videoPlayer.value.load();
  }
});
</script>

<template>
  <transition
    :duration="{ enter: animationInDuration, leave: animationOutDuration }"
    :enter-active-class="
      animationIn ? `animate__animated animate__${animationIn}` : ''
    "
    :leave-active-class="
      animationOut ? `animate__animated animate__${animationOut}` : ''
    "
    appear
  >
    <div
      class="alert-box"
      v-show="show"
      :key="'alert'"
      :class="{
        'direction-bottom': direction === 'bottom',
        'direction-top': direction === 'top',
        'direction-left': direction === 'left',
        'direction-right': direction === 'right',
        'direction-center': direction === 'center',
      }"
      :style="{
        '--animate-duration': `${animationInDuration}ms`,
      }"
    >
      <div class="alert-box-media" v-if="currentAlert.media">
        <img
          class="alert-box-image"
          @load="onMediaLoad"
          @error="onMediaEnd"
          v-if="
            currentAlert.media.type === 'image' ||
            currentAlert.media.type === 'emote' ||
            currentAlert.media.type === 'icon'
          "
          :src="getMediaUrl(currentAlert.media)"
        />
        <video
          class="alert-box-video"
          v-else-if="currentAlert.media.type === 'video'"
          ref="videoPlayer"
          @canplay="onMediaLoad"
          @error="onMediaEnd"
          @ended="onMediaEnd"
          @loadedmetadata="onMediaMetaDataLoaded"
          muted
          :loop="!currentAlert.sound"
          :src="getMediaUrl(currentAlert.media)"
        />
        <component
          :is="templateConfig.component"
          v-else-if="currentAlert.media.type === 'template' && templateConfig"
          class="alert-box-template"
          :streamer-message="streamerMessage"
          :text-style="textStyle"
          :text-border-style="textBorderStyle"
          :font-style="fontStyle"
          :highlight-color="highlightColor"
          :highlight-animation="currentAlert.settings?.textHighlight?.animation"
        />
      </div>

      <!-- Standalone text layer for regular media; a template renders its own. -->
      <div
        v-if="!templateConfig"
        style="display: flex; align-items: center; flex-direction: column"
      >
        <div class="alert-box-text-container">
          <div
            :style="textBorderStyle"
            v-if="streamerMessage && streamerMessage.length"
            class="alert-box-text alert-box-text-border"
          >
            <template v-for="(part, i) in streamerMessage">
              <span :key="'text' + i" v-if="part.type === 'text'">{{
                part.value
              }}</span>
              <span
                :key="'highlight' + i"
                v-else-if="part.type === 'highlight'"
                :style="{
                  ...textBorderStyle,
                  color: highlightColor,
                }"
                class="alert-box-text-highlight animate__animated animate__slower animate__infinite"
                :class="[
                  `animate__${currentAlert.settings.textHighlight.animation}`,
                ]"
              >
                {{ part.value }}
              </span>
            </template>
          </div>
          <div
            :style="textStyle"
            v-if="streamerMessage && streamerMessage.length"
            class="alert-box-text"
          >
            <template v-for="(part, i) in streamerMessage">
              <span :key="'text' + i" v-if="part.type === 'text'">{{
                part.value
              }}</span>
              <span
                :key="'highlight' + i"
                v-else-if="part.type === 'highlight'"
                :style="{
                  ...fontStyle,
                  color: highlightColor,
                }"
                class="alert-box-text-highlight animate__animated animate__slower animate__infinite"
                :class="[
                  `animate__${currentAlert.settings.textHighlight.animation}`,
                ]"
              >
                {{ part.value }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
.alert-box {
  height: 100%;
  width: 100%;
  max-height: 100vh;
  max-width: 100vw;
  display: flex;
  position: relative;
  text-align: center;
  justify-content: center;
  align-items: center;
  padding: 1rem;

  .alert-box-media {
    flex: 1;
    max-height: 100%;
    max-width: 100%;
    min-width: 50%;
    min-height: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    .alert-box-video,
    .alert-box-image {
      display: block;
      min-width: 100%;
      min-height: 100%;
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center bottom;
    }

    .alert-box-template {
      width: 100%;
      height: 100%;
    }
  }

  .alert-box-text-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 75%;
    &.alert-box-text-user-message {
      margin-top: 0.25rem;
    }

    position: relative;

    .alert-box-text {
      position: relative;
      line-height: 1.5 !important;
      width: 100%;

      .alert-box-text-highlight {
        display: inline-block;
        position: relative;
      }

      &.alert-box-text-border {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  &.direction-bottom {
    flex-direction: column;

    .alert-box-text-container {
      text-align: center;
    }
  }

  &.direction-top {
    flex-direction: column-reverse;

    .alert-box-text-container {
      text-align: center;
    }
  }

  &.direction-left {
    flex-direction: row-reverse;

    .alert-box-text-container {
      text-align: right;
      margin-right: 0.5rem;
    }
  }

  &.direction-right {
    flex-direction: row;

    .alert-box-text-container {
      text-align: left;
      margin-left: 0.5rem;
    }
  }

  &.direction-center {
    justify-content: center;
    align-items: center;

    .alert-box-media {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 95%;
      height: 95%;
    }

    .alert-box-text-container {
      width: max-content;
      text-align: center;
    }
  }
}
</style>
